import { Lesson } from "@cs124/lesson";
import { Deadline } from "@cs124/questioner";
import assert from "assert";
import sortBy from "lodash/sortBy";
import sum from "lodash/sum";
import { Array, Boolean, Literal, Number, Record, String } from "runtypes";
import { Metadata, pathFromQuestionerCoordinates } from "./metadata";
export const LessonComponent = Metadata.Or(Deadline);
export const LessonPlan = Record({
  type: Literal("lessonplan"),
  lesson: Lesson,
  components: Array(LessonComponent)
});
const ShareableComponentProgress = Record({
  type: Literal("shareablecomponentprogress"),
  contentType: Literal("video").Or(Literal("walkthrough")),
  path: String,
  id: String,
  viewed: Boolean,
  duration: Number,
  percentage: Number,
  completed: Boolean,
  component: LessonComponent
});
export const ComponentProgress = ShareableComponentProgress;
export const LessonPlanWithProgress = Record({
  lesson: Lesson,
  components: Array(ComponentProgress)
});
export function makeContentMap(contents) {
  const contentByPath = {};
  for (const content of contents) {
    if (!contentByPath[content.language]) {
      contentByPath[content.language] = {};
    }
    if (!contentByPath[content.language][content.path]) {
      contentByPath[content.language][content.path] = [];
    }
    contentByPath[content.language][content.path].push(content);
  }
  for (const language of Object.keys(contentByPath)) {
    for (const path of Object.keys(contentByPath[language])) {
      contentByPath[language][path] = sortBy(
        contentByPath[language][path],
        (c) => c.instructor ? 0 : 1,
        (c) => c.duration * -1
      );
    }
  }
  return contentByPath;
}
export function makeProgressMap(progresses) {
  const progressById = {};
  for (const progress of progresses) {
    assert(!progressById[progress.id]);
    progressById[progress.id] = progress;
  }
  return progressById;
}
export function mergePlanWithProgress(plan, contents, progresses, verbose = false) {
  const contentByPath = makeContentMap(contents);
  const progressById = makeProgressMap(progresses);
  const progressComponents = [];
  for (const planComponent of plan.components) {
    const path = planComponent.type === "deadline" ? pathFromQuestionerCoordinates(planComponent.question) : planComponent.path;
    if (planComponent.type === "metadata" && (path.endsWith(`/intro-${planComponent.semester.toLowerCase()}`) || path.endsWith(`/outro-${planComponent.semester.toLowerCase()}`))) {
      continue;
    }
    const contentType = planComponent.type === "metadata" ? planComponent.contentType : "walkthrough";
    const language = planComponent.type === "metadata" ? planComponent.language : planComponent.question.language;
    const contents2 = contentByPath[language][path];
    if (!contents2) {
      verbose && console.warn(`No contents for ${language}/${path}`);
      continue;
    }
    assert(contents2.length > 0, `No content for ${language}/${path}`);
    const componentProgresses = contents2.map((c) => progressById[c.id]).filter((p) => !!p);
    const instructorContent = contents2.filter((c) => c.instructor).map((c) => c.id);
    const hasInstructor = instructorContent.length > 0;
    const instructorProgress = componentProgresses.filter((p) => instructorContent.includes(p.id));
    const sawInstructor = instructorProgress.length > 0;
    if (componentProgresses.length === 0 || hasInstructor && !sawInstructor) {
      progressComponents.push(
        ComponentProgress.check({
          type: "shareablecomponentprogress",
          contentType,
          component: planComponent,
          path,
          id: contents2[0].id,
          viewed: false,
          duration: contents2[0].duration,
          percentage: 0,
          completed: false
        })
      );
    } else {
      const bestProgress = sortBy(hasInstructor ? instructorProgress : componentProgresses, (p) => p.percentage * -1)[0];
      assert(bestProgress);
      progressComponents.push(
        ComponentProgress.check({
          type: "shareablecomponentprogress",
          contentType,
          component: planComponent,
          path,
          id: bestProgress.id,
          viewed: true,
          duration: bestProgress.duration,
          percentage: bestProgress.percentage,
          completed: bestProgress.completed
        })
      );
    }
  }
  const planWithProgress = LessonPlanWithProgress.check({
    lesson: plan.lesson,
    components: progressComponents
  });
  return planWithProgress;
}
export function lessonCompletedPercentComponents(planProgress, threshold = -1) {
  const completed = planProgress.components.filter(
    (c) => threshold === -1 ? c.completed : c.percentage >= threshold
  ).length;
  const percent = completed / planProgress.components.length;
  assert(percent >= 0 && percent <= 1);
  return percent;
}
export function lessonCompletedPercentDuration(planProgress) {
  const completed = sum(planProgress.components.map((c) => c.percentage / 100 * c.duration));
  const percent = completed / sum(planProgress.components.map((c) => c.duration));
  assert(percent >= 0 && percent <= 1);
  return percent;
}
export function lessonCompletedComponents(planProgress, threshold = -1) {
  return planProgress.components.filter((c) => threshold === -1 ? c.completed : c.percentage >= threshold).length;
}
export function lessonAllComponents(planProgress) {
  return planProgress.components.length;
}
export function lessonCompletedDuration(planProgress, options) {
  const threshold = options?.threshold ?? 100;
  let f = () => true;
  if (options.f) {
    f = options.f;
  }
  return sum(
    planProgress.components.filter((c) => f(c)).map((c) => (c.percentage > threshold ? 1 : c.percentage / 100) * c.duration)
  );
}
export function lessonAllDuration(planProgress, f = () => true) {
  return sum(planProgress.components.filter((c) => f(c)).map((c) => c.duration));
}
//# sourceMappingURL=lessonplan.js.map
